import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchChatSessions,
  fetchFolders,
  fetchFolderSessions,
  deleteSession,
  renameSession,
  deleteFolder,
  renameFolder,
  createFolder,
  moveSession,
  ChatSession,
  ChatFolder,
} from "../../../services/api/chatHistory.api";
import { removeChatSelections } from "../../Chat/utils/chatSelectionsStorage";

export interface FolderWithSessions extends ChatFolder {
  sessions: ChatSession[];
  isOpen: boolean;
  isLoading: boolean;
  // true once we've successfully fetched sessions at least once (even if empty).
  // Prevents the re-fetch effect from looping on empty folders.
  hasFetched: boolean;
}

interface UseChatHistoryReturn {
  sessions: ChatSession[];
  folders: FolderWithSessions[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  toggleFolder: (folderId: number) => void;
  handleDeleteSession: (sessionId: string) => Promise<void>;
  handleRenameSession: (sessionId: string, newName: string) => Promise<void>;
  handleDeleteFolder: (folderId: number) => Promise<void>;
  handleRenameFolder: (folderId: number, newName: string) => Promise<void>;
  handleCreateFolder: (name: string) => Promise<void>;
  handleMoveSession: (sessionId: string, folderId: number | null) => Promise<void>;
}

export function useChatHistory(userEmail: string | null): UseChatHistoryReturn {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [folders, setFolders] = useState<FolderWithSessions[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Tracks which folders are currently being fetched to prevent duplicate calls
  const fetchingFolderIds = useRef<Set<number>>(new Set());

  // Incrementing refreshTick triggers the fetch useEffect below.
  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  // ── Shared folder session fetcher ─────────────────────────────────────────
  // Single entry point for fetching sessions inside a folder.
  // Uses fetchingFolderIds ref to prevent duplicate in-flight requests.
  // Sets hasFetched=true after completion (even on empty result) so the
  // re-fetch effect never loops on empty folders.
  const fetchSessionsForFolder = useCallback(
    async (folderId: number) => {
      if (!userEmail) return;
      if (fetchingFolderIds.current.has(folderId)) return; // already in flight

      fetchingFolderIds.current.add(folderId);
      setFolders((prev) =>
        prev.map((f) => (f.id === folderId ? { ...f, isLoading: true } : f))
      );

      try {
        const fetchedSessions = await fetchFolderSessions(userEmail, folderId);
        setFolders((prev) =>
          prev.map((f) =>
            f.id === folderId
              ? { ...f, sessions: fetchedSessions, isLoading: false, hasFetched: true }
              : f
          )
        );
      } catch {
        setFolders((prev) =>
          prev.map((f) =>
            f.id === folderId
              ? { ...f, isLoading: false, hasFetched: true }
              : f
          )
        );
      } finally {
        fetchingFolderIds.current.delete(folderId);
      }
    },
    [userEmail]
  );

  // ── Fetch sessions and folders ────────────────────────────────────────────
  // Runs on mount and whenever refresh() is called.
  // For open folders: clears sessions and resets hasFetched so the re-fetch
  // effect picks them up fresh after a move/remove/add.
  useEffect(() => {
    if (!userEmail) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    Promise.all([fetchChatSessions(userEmail), fetchFolders(userEmail)])
      .then(([sessionsData, foldersData]) => {
        if (cancelled) return;
        setSessions(sessionsData);
        setFolders((prev) =>
          foldersData.map((f) => {
            const existing = prev.find((p) => p.id === f.id);
            return {
              ...f,
              isOpen: existing?.isOpen ?? false,
              // Clear sessions for open folders so re-fetch effect refreshes them.
              // Reset hasFetched too so the effect knows to fetch again.
              sessions: existing?.isOpen ? [] : (existing?.sessions ?? []),
              isLoading: false,
              hasFetched: existing?.isOpen ? false : (existing?.hasFetched ?? false),
            };
          })
        );
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userEmail, refreshTick]);

  // ── Re-fetch sessions for open folders after a refresh ───────────────────
  // Fires when folders state changes. Fetches sessions for any folder that is:
  //   - open
  //   - has no sessions
  //   - is not already loading
  //   - has NOT been fetched yet (hasFetched=false)
  // The hasFetched flag prevents infinite loops when a folder is genuinely empty.
  useEffect(() => {
    if (!userEmail) return;

    folders.forEach((f) => {
      if (f.isOpen && f.sessions.length === 0 && !f.isLoading && !f.hasFetched) {
        void fetchSessionsForFolder(f.id);
      }
    });
  }, [folders, userEmail, fetchSessionsForFolder]);

  // ── Toggle folder open/closed ─────────────────────────────────────────────
  // Flips isOpen immediately for responsive UI, then fetches sessions if opening.
  const toggleFolder = useCallback(
    (folderId: number) => {
      setFolders((prev) => {
        const folder = prev.find((f) => f.id === folderId);
        if (!folder) return prev;

        const willBeOpen = !folder.isOpen;

        // If opening, kick off a fetch.
        // fetchSessionsForFolder handles dedup via fetchingFolderIds ref.
        if (willBeOpen) {
          void fetchSessionsForFolder(folderId);
        }

        return prev.map((f) =>
          f.id === folderId ? { ...f, isOpen: willBeOpen } : f
        );
      });
    },
    [fetchSessionsForFolder]
  );

  // ── Session actions ───────────────────────────────────────────────────────

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      if (!userEmail) return;
      // A delete that can't reach the server fails like a refused one:
      // nothing changes here, and nothing is thrown at the caller (the
      // sidebar moves on to a new chat after any delete).
      let ok = false;
      try {
        ok = await deleteSession(sessionId, userEmail);
      } catch (e) {
        console.error('[chat-history] Could not delete the chat:', e);
      }
      if (ok) {
        // The chat is gone, so its saved picks (see chatSelectionsStorage) are too.
        removeChatSelections(sessionId);
        refresh();
      }
    },
    [userEmail, refresh]
  );

  const handleRenameSession = useCallback(
    async (sessionId: string, newName: string) => {
      if (!userEmail) return;
      const ok = await renameSession(sessionId, userEmail, newName);
      if (ok) {
        refresh();
      }
    },
    [userEmail, refresh]
  );

  // ── Folder actions ────────────────────────────────────────────────────────

  const handleDeleteFolder = useCallback(
    async (folderId: number) => {
      if (!userEmail) return;
      const ok = await deleteFolder(folderId, userEmail);
      if (ok) {
        refresh();
      }
    },
    [userEmail, refresh]
  );

  const handleRenameFolder = useCallback(
    async (folderId: number, newName: string) => {
      if (!userEmail) return;
      const ok = await renameFolder(folderId, userEmail, newName);
      if (ok) {
        refresh();
      }
    },
    [userEmail, refresh]
  );

  const handleCreateFolder = useCallback(
    async (name: string) => {
      if (!userEmail) return;
      await createFolder(userEmail, name);
      refresh();
    },
    [userEmail, refresh]
  );

  const handleMoveSession = useCallback(
    async (sessionId: string, folderId: number | null) => {
      if (!userEmail) return;
      const ok = await moveSession(sessionId, userEmail, folderId);
      if (ok) {
        refresh();
      }
    },
    [userEmail, refresh]
  );

  return {
    sessions,
    folders,
    isLoading,
    error,
    refresh,
    toggleFolder,
    handleDeleteSession,
    handleRenameSession,
    handleDeleteFolder,
    handleRenameFolder,
    handleCreateFolder,
    handleMoveSession,
  };
}